import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Switch
} from '@fluentui/react-components';
import { Add20Regular, ArrowTurnDownRight20Regular, Dismiss16Regular, Search20Regular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useTranslation } from 'react-i18next';

import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import MedicalServiceSettingsFormComponent from '../MedicalServiceSettingsForm/MedicalServiceSettingsFormComponent';

// guía
import { GuideTable, GuideTh, GuideRowActions, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuidePagination } from '../../../theme/components/GuidePagination';
import { GuideSegmented } from '../../../theme/components/GuideSegmented';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { StatusPill } from '../../../theme/components/StatusPill';

// lógica
import {
  AvailabilityFilter,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZES,
  SEARCH_DEBOUNCE_MS,
  SEARCH_MAX,
  availabilityToParam,
  clampPageNumber,
  normalizeQuery,
  snomedLabel,
  specialtyNames
} from './MedicalServiceList.Logic';

import * as ComponentFunctions from './MedicalService.Function';
import './MedicalServiceListComponent.css';

/**
 * Listado de servicios médicos (guía) con búsqueda y paginado reales: cada cambio de texto
 * (con espera de 400 ms), de filtro de disponibilidad, de página o de tamaño pide al servidor
 * sólo esa página. La grilla muestra servicio y comentario, SNOMED, especialidades, estado con
 * interruptor y acciones en ⋯ (Editar, Configuración, Eliminar con confirmación).
 */
const MedicalServiceListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [searchText, setSearchText] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [availability, setAvailability] = useState<AvailabilityFilter>('all');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  // jerarquía: sólo primer nivel, o los sub-servicios de un padre (se entra desde la fila)
  const [firstLevelOnly, setFirstLevelOnly] = useState<boolean>(false);
  const [parentFilter, setParentFilter] = useState<{ id: number; name: string } | undefined>(undefined);

  const [selectedMedicalService, setSelectedMedicalService] = useState<AvailableMedicalService | undefined>(undefined);
  const [isSettingsFormOpen, { setTrue: openSettingsFormPanel, setFalse: dismissSettingsFormPanel }] = useBoolean(false);
  const [deleting, setDeleting] = useState<AvailableMedicalService | undefined>(undefined);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [statusPendingId, setStatusPendingId] = useState<number | undefined>(undefined);

  const query = normalizeQuery({
    pageNumber,
    pageSize,
    searchText: appliedSearch,
    available: availabilityToParam(availability),
    // Con un padre elegido se listan sus sub-servicios; "sólo primer nivel" no aplica ahí.
    firstLevelOnly: parentFilter ? undefined : firstLevelOnly,
    parentServiceId: parentFilter?.id
  });

  // La búsqueda la resuelve el servidor: se espera a que la escritura se detenga.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchText.trim());
      setPageNumber(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    props.GetMedicalServicesPage(query);
  }, [query.pageNumber, query.pageSize, query.searchText, query.available, query.firstLevelOnly, query.parentServiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tras borrar, o si la página quedó fuera de rango, se vuelve a pedir al servidor.
  const previousDeleting = useRef(false);
  useEffect(() => {
    if (previousDeleting.current && !props.isDeleting) {
      if (!props.failOnDeleting) {
        setDeleting(undefined);
        setDeleteRequested(false);
        const next = clampPageNumber(pageNumber, Math.ceil(Math.max(0, props.Page.totalRecords) / pageSize));
        if (next !== pageNumber) setPageNumber(next);
        else props.GetMedicalServicesPage(query, true);
      }
    }
    previousDeleting.current = !!props.isDeleting;
  }, [props.isDeleting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alta o edición desde el drawer de la página: la página actual puede haber cambiado.
  const previousSaving = useRef(false);
  useEffect(() => {
    if (previousSaving.current && !props.isSaving && (props.savedSuccessfully || props.addedSuccessfully)) {
      props.GetMedicalServicesPage(query, true);
    }
    previousSaving.current = !!props.isSaving;
  }, [props.isSaving]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!props.isChangingStatus) setStatusPendingId(undefined);
  }, [props.isChangingStatus]);

  // events
  const onChangeStatus = (service: AvailableMedicalService, enabled: boolean) => {
    setStatusPendingId(service.id);
    props.ChangeMedicalServiceStatus(service.id, enabled);
  };

  const onChangeSettings = (service: AvailableMedicalService) => {
    setSelectedMedicalService(service);
    openSettingsFormPanel();
  };

  const confirmDelete = () => {
    if (!deleting) return;
    setDeleteRequested(true);
    props.DeleteMedicalService(deleting.id);
  };

  const clearFilters = () => {
    setSearchText('');
    setAvailability('all');
    setFirstLevelOnly(false);
    setParentFilter(undefined);
    setPageNumber(1);
  };

  const page = props.Page;
  const isFiltering = !!query.searchText || availability !== 'all' || !!query.firstLevelOnly || !!parentFilter;
  const showSkeleton = page.isLoading && !page.loaded;

  const renderRow = (item: AvailableMedicalService) => {
    const snomed = snomedLabel(item);
    const specialties = specialtyNames(item);
    const childrenCount = item.children?.length ?? 0;
    const statusBusy = statusPendingId === item.id && props.isChangingStatus;
    return (
      <tr key={item.id} className={guideRowClassName({ inactive: !item.available })} data-testid={`msl-row-${item.id}`}>
        <td className={cell('num')}>
          {item.internalCode
            ? <span className="msl-code">{item.internalCode}</span>
            : <span className="msl-muted" title={`#${item.id}`}>{t('medicalServiceList.hierarchy.noCode')}</span>}
        </td>
        <td>
          <div className="msl-service">
            <span className="msl-service__name">{item.serviceNameReference}</span>
            {item.comments && <span className="msl-service__comment">{item.comments}</span>}
            {item.parent && !parentFilter && (
              <span className="msl-service__parent">
                <ArrowTurnDownRight20Regular />
                {t('medicalServiceList.hierarchy.childOf', { name: item.parent.serviceNameReference })}
              </span>
            )}
            {childrenCount > 0 && (
              <button type="button" className="msl-service__children" onClick={() => { setParentFilter({ id: item.id, name: item.serviceNameReference }); setPageNumber(1); }} data-testid={`msl-children-${item.id}`}>
                {t('medicalServiceList.hierarchy.children', { count: childrenCount })}
              </button>
            )}
          </div>
        </td>
        <td>
          {snomed
            ? <span className="msl-snomed">{snomed}</span>
            : <span className="msl-muted">{t('medicalServiceList.props.notLinked')}</span>}
        </td>
        <td>
          {item.category
            ? <StatusPill tone="neutral" symbol="" size="small">{item.category.name}</StatusPill>
            : <span className="msl-muted">{t('medicalServiceList.hierarchy.noCategory')}</span>}
        </td>
        <td>
          {specialties.length > 0 ? (
            <div className="msl-specialties">
              {specialties.map((name) => <StatusPill key={name} tone="info" symbol="" size="small">{name}</StatusPill>)}
            </div>
          ) : (
            <span className="msl-muted">{t('medicalServiceList.props.noSpecialties')}</span>
          )}
        </td>
        <td>
          <div className="msl-scope">
            {item.isAssistanceScope && <StatusPill tone="info" symbol="" size="small">{t('medicalServiceList.scope.assistance')}</StatusPill>}
            {item.isProtocolScope && <StatusPill tone="confirmed" symbol="" size="small">{t('medicalServiceList.scope.protocol')}</StatusPill>}
            {!item.isAssistanceScope && !item.isProtocolScope && <span className="msl-muted">{t('medicalServiceList.scope.none')}</span>}
          </div>
        </td>
        <td>
          <span className="msl-status">
            <Switch
              checked={!!item.available}
              disabled={statusBusy}
              label={item.available ? t('medicalServiceList.props.enable') : t('medicalServiceList.props.disabled')}
              aria-label={t('medicalServiceList.actions.toggleStatus', { name: item.serviceNameReference })}
              onChange={(_event, data) => onChangeStatus(item, data.checked)}
              data-testid={`msl-status-${item.id}`}
            />
            {statusBusy && <Spinner size="extra-tiny" />}
          </span>
        </td>
        <td className={cell('actions')}>
          <GuideRowActions
            label={t('medicalServiceList.actions.rowActions', { name: item.serviceNameReference })}
            actions={[
              { key: 'edit', label: t('medicalServiceList.props.modify'), onClick: () => props.editEvent?.(item) },
              { key: 'settings', label: t('medicalServiceList.props.config'), onClick: () => onChangeSettings(item) },
              { key: 'children', label: t('medicalServiceList.hierarchy.viewChildren'), disabled: childrenCount === 0, onClick: () => { setParentFilter({ id: item.id, name: item.serviceNameReference }); setPageNumber(1); } },
              { key: 'delete', label: t('medicalServiceList.confirmationButton.deleteButton'), danger: true, onClick: () => setDeleting(item) }
            ]}
          />
        </td>
      </tr>
    );
  };

  const renderBody = () => {
    if (showSkeleton) return <GuideSkeleton rows={6} label={t('medicalServiceList.props.loadingMedicalServices')} data-testid="msl-loading" />;
    if (page.failed && !page.loaded) return null;
    if (page.loaded && page.items.length === 0) {
      return isFiltering ? (
        <GuideEmptyState
          title={t('medicalServiceList.empty.noMatchTitle')}
          text={t('medicalServiceList.empty.noMatchText')}
          action={<GuideLinkButton onClick={clearFilters}>{t('medicalServiceList.empty.clearFilters')}</GuideLinkButton>}
          data-testid="msl-no-match"
        />
      ) : (
        <GuideEmptyState
          title={t('medicalServiceList.props.registeredMedicalServices')}
          text={t('medicalServiceList.empty.text')}
          action={props.onAdd && <Button appearance="primary" icon={<Add20Regular />} onClick={props.onAdd}>{t('medicalServicesPage.button.addMedicalService')}</Button>}
          data-testid="msl-empty"
        />
      );
    }
    return (
      <div className={`msl-table${page.isLoading ? ' is-refreshing' : ''}`} aria-busy={page.isLoading || undefined}>
        <GuideTable
          ariaLabel={t('medicalServicesPage.medicalServicesTitle')}
          data-testid="msl-table"
          footer={
            <GuidePagination
              page={page.pageNumber}
              pageSize={page.pageSize}
              total={page.totalRecords}
              onPageChange={(next) => setPageNumber(next)}
              pageSizeOptions={PAGE_SIZES}
              onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
            />
          }
        >
          <thead>
            <tr>
              <GuideTh className="msl-col-code">{t('medicalServiceList.props.code')}</GuideTh>
              <GuideTh>{t('medicalServiceList.props.medicalPractice')}</GuideTh>
              <GuideTh>{t('medicalServiceList.props.snowmedCode')}</GuideTh>
              <GuideTh>{t('medicalServiceList.props.category')}</GuideTh>
              <GuideTh>{t('medicalServiceList.props.specialtiesColumn')}</GuideTh>
              <GuideTh>{t('medicalServiceList.props.scope')}</GuideTh>
              <GuideTh>{t('medicalServiceList.props.state')}</GuideTh>
              <GuideTh className={cell('actions')}><span className="msl-sr-only">{t('medicalServiceList.props.options')}</span></GuideTh>
            </tr>
          </thead>
          <tbody>{page.items.map(renderRow)}</tbody>
        </GuideTable>
      </div>
    );
  };

  return (
    <div className="msl" data-testid="msl-root">
      <div className="msl-toolbar">
        <Input
          type="search"
          className="msl-search"
          contentBefore={<Search20Regular />}
          placeholder={t('medicalServiceList.props.filterByServiceName')}
          aria-label={t('medicalServiceList.props.filterByServiceName')}
          value={searchText}
          maxLength={SEARCH_MAX}
          onChange={(_event, data) => setSearchText(data.value)}
          data-testid="msl-search"
        />
        <GuideSegmented<AvailabilityFilter>
          ariaLabel={t('medicalServiceList.filters.availability')}
          value={availability}
          onChange={(value) => { setAvailability(value); setPageNumber(1); }}
          options={[
            { value: 'all', label: t('medicalServiceList.filters.all') },
            { value: 'enabled', label: t('medicalServiceList.props.enable') },
            { value: 'disabled', label: t('medicalServiceList.props.disabled') }
          ]}
          data-testid="msl-availability"
        />
        {parentFilter ? (
          <span className="msl-parent-chip" data-testid="msl-parent-chip">
            <ArrowTurnDownRight20Regular />
            {t('medicalServiceList.filters.childrenOf', { name: parentFilter.name })}
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss16Regular />}
              aria-label={t('medicalServiceList.filters.clearParent')}
              title={t('medicalServiceList.filters.clearParent')}
              onClick={() => { setParentFilter(undefined); setPageNumber(1); }}
            />
          </span>
        ) : (
          <Switch
            label={t('medicalServiceList.filters.firstLevelOnly')}
            checked={firstLevelOnly}
            onChange={(_event, data) => { setFirstLevelOnly(data.checked); setPageNumber(1); }}
            data-testid="msl-first-level"
          />
        )}
        <div className="msl-toolbar__counters">
          {page.loaded && <GuideCountChip label={isFiltering ? t('medicalServiceList.toolbar.matching') : t('medicalServiceList.toolbar.total')} count={page.totalRecords} />}
          {page.isLoading && page.loaded && <Spinner size="extra-tiny" aria-label={t('medicalServiceList.props.loadingMedicalServices')} />}
        </div>
        {props.onAdd && (
          <div className="msl-toolbar__actions">
            <Button appearance="primary" icon={<Add20Regular />} onClick={props.onAdd} data-testid="msl-add">
              {t('medicalServicesPage.button.addMedicalService')}
            </Button>
          </div>
        )}
      </div>

      {page.failed && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="msl-error">
          <MessageBarBody>
            <MessageBarTitle>{t('medicalServiceList.errors.load')}</MessageBarTitle>
            {page.error?.ErrorMessage}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => props.GetMedicalServicesPage(query, true)}>{t('medicalServiceList.errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {props.failOnChangingStatus && !props.isChangingStatus && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="msl-status-error">
          <MessageBarBody>
            <MessageBarTitle>{t('medicalServiceList.errors.status')}</MessageBarTitle>
            {props.error?.ErrorMessage}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {renderBody()}

      {selectedMedicalService && (
        <MedicalServiceSettingsFormComponent
          isOpen={isSettingsFormOpen}
          openPanel={openSettingsFormPanel}
          dismissPanel={dismissSettingsFormPanel}
          MedicalServiceId={selectedMedicalService.id}
        />
      )}

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !props.isDeleting) { setDeleting(undefined); setDeleteRequested(false); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('medicalServiceList.confirmationButton.deleteInformation')}</DialogTitle>
            <DialogContent>
              <p className="msl-dialog__text">{t('medicalServiceList.confirmationButton.descriptionInformation')}</p>
              <p className="msl-dialog__name">
                <span className="msl-dialog__label">{t('medicalServiceList.confirmationButton.medicalServiceText')}</span> {deleting?.serviceNameReference}
              </p>
              {deleteRequested && props.failOnDeleting && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="msl-delete-error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('medicalServiceList.errors.delete')}</MessageBarTitle>
                    {props.error?.ErrorMessage}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setDeleting(undefined); setDeleteRequested(false); }} disabled={props.isDeleting}>
                {t('medicalServiceList.confirmationButton.cancelButton')}
              </Button>
              <Button
                appearance="primary"
                className="msl-danger"
                icon={props.isDeleting ? <Spinner size="extra-tiny" /> : undefined}
                disabled={props.isDeleting}
                onClick={confirmDelete}
                data-testid="msl-delete-confirm"
              >
                {t('medicalServiceList.confirmationButton.deleteButton')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(MedicalServiceListComponent as any);
